import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import profileApi from "../services/profile.api";
import { mapEducationFormToApi } from "../mappers/EducationMapper";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import deleteIcon from "../../../assets/delete-icon.png";
import viewIcon from "../../../assets/view-icon.png";
import {
  hasDateCollision,
  hasDateCollisionAcrossProfiles,
  isValidCollegeName,
  validateEndDateAfterStart,
} from "../../../shared/utils/validation";
import bulbIcon from "../../../assets/bulb-icon.png";
import { forwardRef, useImperativeHandle } from "react";
import { removeParsedEducationById } from "../store/resumeSlice";
import greenCheck from "../../../assets/green-check.png";
import { handleEyeClick } from "../../../shared/utils/fileDownload";
import IdProofUploadModal from "./IdProofUploadModal";
import masterApi from "../../../services/master.api";
const EducationForm = forwardRef((props, ref) => {
  const {
    educationId,
    onEducationLevelChange,
    fixedEducationLevelId,
    fixedDocumentTypeId,
    disableEducationLevel = false,
    showSpecialization = true,
    showBoard = true,
    existingData = null,
    masterData,
    refreshEducation,
    existingRanges = [],
    onDirtyChange = () => {},
    parsedId = null,
    onSave = () => {}, //  Callback to keep accordion open after save
    experienceList = [], //  Experience list for cross-profile date collision check
    educationList = [], //  Education list for cross-profile date collision check
    activeStep,
  } = props;
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user?.data);
  const candidateId = user?.user?.id;
  const fileInputRef = useRef(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    university: "",
    universityName: "",
    otherQualification: "",
    college: "",
    degree: "Intermediate",
    from: "",
    to: "",
    percentage: "",
    specialization: "",
    otherSpecialization: "",
    otherSpecialization2: "",
    specialization2: "",
    educationType: "",
    educationLevel: "",
    educationDocCode: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [localDirty, setLocalDirty] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [integratedSpecializations, setIntegratedSpecializations] = useState({
    0: [],
    1: [],
  });
  const [integratedSpecialization1, setIntegratedSpecialization1] =
    useState("");
  const [integratedSpecialization2, setIntegratedSpecialization2] =
    useState("");

  useEffect(() => {
    if (!existingData) return;

    if (existingData?.document) {
      setExistingDocument(existingData.document);
    }

    setFormData((prev) => ({
      ...prev,
      ...existingData,
    }));

    // Restore integrated specializations
    setIntegratedSpecialization1(existingData.specialization || "");

    setIntegratedSpecialization2(existingData.specialization2 || "");
    setFormErrors({});
  }, [existingData]);
  useEffect(() => {
    if (!existingData?.university) return;
    if (!masterData?.boards?.length) return;

    const selectedCourse = masterData.boards.find(
      (b) => b.educationQualificationsId === existingData.university,
    );

    if (!selectedCourse?.isIntegratedCourse) {
      setIntegratedSpecializations({
        0: [],
        1: [],
      });
      return;
    }

    const icGroups = Array.isArray(selectedCourse.icGroups)
      ? selectedCourse.icGroups
      : [];

    if (!icGroups.length) return;

    fetchIntegratedSpecializations(icGroups, true);
  }, [existingData, masterData?.boards]);
  useEffect(() => {
    if (existingData) return;
    const fixedId = fixedEducationLevelId || fixedDocumentTypeId;
    if (!fixedId || !masterData.educationLevels.length) return;
    const selected = masterData.educationLevels.find(
      (el) => el.documentTypeId === fixedId,
    );
    if (!selected) return;
    setFormData((prev) => ({
      ...prev,
      educationLevel: fixedId,
      educationDocCode: selected.docCode,
    }));
  }, [
    fixedEducationLevelId,
    fixedDocumentTypeId,
    masterData.educationLevels,
    existingData,
  ]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    clearErrors(id);

    if (id === "from" || id === "to") {
      clearErrors("dateRange");
    }

    if (id === "educationType") {
      clearErrors("educationType", "dateRange");
    }

    if (id === "educationLevel") {
      const selected = masterData.educationLevels.find(
        (el) => el.documentTypeId === value,
      );

      setFormData((prev) => ({
        ...prev,
        educationDocCode: selected?.docCode || "",
        university: "",
      }));
      setCertificateFile(null);
      setExistingDocument(null);

      clearErrors("educationLevel", "university", "certificateFile");

      if (educationId && onEducationLevelChange && selected) {
        onEducationLevelChange(educationId, selected.documentName);
      }
    }

    if (id === "university") {
      const selected = masterData?.boards?.find(
        (b) => b.educationQualificationsId === value,
      );

      const isOthers = selected?.qualificationName?.toLowerCase() === "others";

      const isIntegratedCourse = selected?.isIntegratedCourse === true;

      setFormData((prev) => ({
        ...prev,
        university: value,
        specialization: "",
        specialization2: "",
        otherSpecialization: "",
        otherSpecialization2: "",
        otherQualification: isOthers ? prev.otherQualification : "",
      }));

      if (isIntegratedCourse) {
        const icGroups = Array.isArray(selected?.icGroups)
          ? selected.icGroups
          : [];

        fetchIntegratedSpecializations(icGroups);
      } else {
        setIntegratedSpecializations([]);
        setIntegratedSpecialization1("");
        setIntegratedSpecialization2("");
      }
    }

    if (id === "specialization") {
      setFormData((prev) => ({
        ...prev,
        specialization: value,
        otherSpecialization: "",
      }));
    }

    setLocalDirty(true);
    onDirtyChange(true);
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    clearErrors("certificateFile");

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));

    const isValidType =
      allowedTypes.includes(file.type) &&
      allowedExtensions.includes(fileExtension);

    if (!isValidType) {
      setFormErrors((prev) => ({
        ...prev,
        certificateFile: "Only PDF, JPG or PNG files are allowed",
      }));

      e.target.value = "";
      setCertificateFile(null);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setFormErrors((prev) => ({
        ...prev,
        certificateFile: "File size must be under 2MB",
      }));

      e.target.value = "";
      setCertificateFile(null);
      return;
    }

    setCertificateFile(file);
    markDirty();
  };

  const markDirty = () => {
    setLocalDirty(true);
    onDirtyChange(true);
  };

  const formatFileSize = (size) => {
    if (!size) return "";
    const kb = size / 1024;
    if (kb < 1024) return kb.toFixed(1) + " KB";
    return (kb / 1024).toFixed(1) + " MB";
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    const requiredFields = [
      { key: "educationLevel", label: "Education Level" },
      { key: "college", label: "School / College / University" },
      // { key: 'from', label: 'From Date' },
      { key: "to", label: "To Date" },
      { key: "percentage", label: "Percentage" },
      ...(showBoard ? [{ key: "university", label: "Board" }] : []),
      // ...(showSpecialization ? [{ key: 'specialization', label: 'Specialization' }] : []),
      { key: "educationType", label: "Education Type" },
      ...(!isBoardLevel ? [{ key: "from", label: "From Date" }] : []),
    ];
    requiredFields.forEach((field) => {
      if (!formData[field.key]?.toString().trim()) {
        errors[field.key] = `This field is required`;
        isValid = false;
      }
    });

    if (!formData.college?.trim()) {
      errors.college = "This field is required";
      isValid = false;
    } else if (!isValidCollegeName(formData.college)) {
      errors.college = "Only alphabets and spaces are allowed";
      isValid = false;
    }

    if (formData.universityName?.trim()) {
      if (!isValidCollegeName(formData.universityName)) {
        errors.universityName = "Only alphabets and spaces are allowed";
        isValid = false;
      }
    }

    if (isOthersSelected && !formData.otherQualification?.trim()) {
      errors.otherQualification = "This field is required";
      isValid = false;
    }

    if (
      isOtherSpecializationSelected &&
      !formData.otherSpecialization?.trim()
    ) {
      errors.otherSpecialization = "This field is required";
      isValid = false;
    }

    if (
      isOtherIntegratedSpecialization2 &&
      !formData.otherSpecialization2?.trim()
    ) {
      errors.otherSpecialization2 = "This field is required";
      isValid = false;
    }

    if (formData.from && formData.to) {
      const { isValid: isDateValid, error } = validateEndDateAfterStart(
        formData.from,
        formData.to,
      );
      if (!isDateValid) {
        errors.dateRange = error || "Invalid education date range";
        isValid = false;
      }
    }

    const pct = Number(formData.percentage);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      errors.percentage = "Enter a valid percentage between 0 and 100";
      isValid = false;
    }

    if (!certificateFile && !existingDocument) {
      errors.certificateFile = "Certificate file is required";
      isValid = false;
    }
    setFormErrors(errors);
    if (!isValid) {
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        document
          .getElementById(firstError)
          ?.scrollIntoView({ behavior: "smooth" });
      }
    }
    return { isValid, errors };
  };

  useImperativeHandle(ref, () => ({
    validate: () => validateForm(),
  }));

  const handlePercentageChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setFormData((prev) => ({ ...prev, percentage: "" }));
      clearErrors("percentage");
      markDirty();
      return;
    }

    if (!/^\d{0,3}(\.\d{0,2})?$/.test(value)) return;

    setFormData((prev) => ({ ...prev, percentage: value }));
    clearErrors("percentage");
    markDirty();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    const { isValid } = validateForm();

    if (!isValid) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    const boardLevelIds = masterData.educationLevels
      .filter((e) => e.docCode === "BOARD")
      .map((e) => e.documentTypeId);

    const interLevelIds = masterData.educationLevels
      .filter((e) => e.docCode === "INTER")
      .map((e) => e.documentTypeId);

    const isCurrentBoard = boardLevelIds.includes(formData.educationLevel);
    const isCurrentInter = interLevelIds.includes(formData.educationLevel);

    // Find existing BOARD & INTER entries
    const existingBoard = educationList.find((e) =>
      boardLevelIds.includes(e.educationLevelId || e.data?.educationLevel),
    );

    const existingInter = educationList.find((e) =>
      interLevelIds.includes(e.educationLevelId || e.data?.educationLevel),
    );

    // Convert to Date
    const currentTo = formData.to ? new Date(formData.to) : null;
    const boardTo = isCurrentBoard
      ? currentTo
      : existingBoard?.data?.to
        ? new Date(existingBoard.data.to)
        : null;

    const interTo = isCurrentInter
      ? currentTo
      : existingInter?.data?.to
        ? new Date(existingInter.data.to)
        : null;

    // 🔴 VALIDATION
    if (boardTo && interTo) {
      // Rule 1: INTER must be after BOARD
      if (interTo <= boardTo) {
        setFormErrors((prev) => ({
          ...prev,
          to: "12th/Intermediate completion date must be after 10th/Board completion date",
        }));
        return;
      }

      // Rule 2: Minimum 1 year gap
      const minInterDate = new Date(boardTo);
      minInterDate.setFullYear(minInterDate.getFullYear() + 1);

      if (interTo < minInterDate) {
        setFormErrors((prev) => ({
          ...prev,
          to: "There must be at least a 1 year gap between 10th/Board and 12th/Intermediate completion dates",
        }));
        return;
      }
    }

    const isBoard = boardLevelIds.includes(formData.educationLevel);
    const isInter = interLevelIds.includes(formData.educationLevel);
    const isHigherEducation = !isBoard && !isInter;

    // Find latest school-level date (INTER preferred, else BOARD)
    let schoolMaxDate = null;

    if (existingInter?.data?.to) {
      schoolMaxDate = new Date(existingInter.data.to);
    } else if (existingBoard?.data?.to) {
      schoolMaxDate = new Date(existingBoard.data.to);
    }

    // If editing current form itself
    if (isInter && currentTo) {
      schoolMaxDate = currentTo;
    }
    if (isBoard && currentTo && !existingInter) {
      schoolMaxDate = currentTo;
    }

    if (isHigherEducation && schoolMaxDate) {
      const fromDate = formData.from ? new Date(formData.from) : null;

      if (fromDate && fromDate <= schoolMaxDate) {
        setFormErrors((prev) => ({
          ...prev,
          from: "This must be after your 10th and Intermediate completion date",
        }));
        return;
      }
    }

    const fromDate = isBoardLevel ? formData.to : formData.from;

    const collision = hasDateCollision({
      from: fromDate,
      to: formData.to,
      educationId,
      existingRanges,
    });

    if (collision) {
      setFormErrors((prev) => ({
        ...prev,
        dateRange: "These dates overlap with another education entry",
      }));
      // setLoading(false);
      setIsSaving(false);
      return;
    }

    const selectedEducationType = masterData.educationTypes.find(
      (e) => e.educationTypeId === formData.educationType,
    );

    const isFullTime =
      selectedEducationType?.educationType?.toLowerCase() === "full time";

    if (isFullTime) {
      const crossProfileCollision = hasDateCollisionAcrossProfiles({
        from: fromDate,
        to: formData.to,
        excludeId: educationId,
        educationList: educationList || [],
        experienceList: experienceList || [],
        presentlyWorking: false,
      });

      if (crossProfileCollision) {
        setFormErrors((prev) => ({
          ...prev,
          dateRange: "These dates overlap with a work experience entry",
        }));
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(true);

    try {
      const selectedEducationLevel = masterData.educationLevels.find(
        (level) => level.documentTypeId === formData.educationLevel,
      );
      const docCode = selectedEducationLevel?.docCode;
      if (!docCode) {
        toast.error("Education level docCode not found");
        setIsSaving(false);
        return;
      }
      const isIntegratedCourse = selectedBoard?.isIntegratedCourse === true;
      const normalizedFormData = {
        ...formData,
        percentage: Number(formData.percentage),
        college: formData.college?.replace(/\s+/g, " ").trim(),
        university: formData.university?.replace(/\s+/g, " ").trim(),
        universityName: formData.universityName?.replace(/\s+/g, " ").trim(),
        specialization: isIntegratedCourse
          ? integratedSpecialization1
          : formData.specialization,

        // Specialization 2
        specialization2: isIntegratedCourse ? integratedSpecialization2 : "",
        otherSpecialization: isIntegratedCourse
          ? isOtherIntegratedSpecialization1
            ? formData.otherSpecialization?.trim()
            : ""
          : isOtherSpecializationSelected
            ? formData.otherSpecialization?.trim()
            : "",

        // Specialization 2 -> new backend field
        otherSpecialization2:
          isIntegratedCourse && isOtherIntegratedSpecialization2
            ? formData.otherSpecialization2?.trim()
            : "",
        otherQualification: isOthersSelected
          ? formData.otherQualification?.trim()
          : "",
      };
      let isValidationPending = false;
      let pendingChecks = null;

      if (certificateFile && docCode === "BOARD") {
        const res = await profileApi.ValidateDocument(docCode, certificateFile);

        if (res?.data === true) {
          isValidationPending = false;
          pendingChecks = null;
        } else {
          isValidationPending = true;
          pendingChecks = ["document"];
        }
      }

      const payload = mapEducationFormToApi({
        formData: normalizedFormData,
        candidateId,
        fixedEducationLevelId,
        educationId,
        isOthersSelected,
        isOtherSpecializationSelected,
        isValidationPending,
        pendingChecks,
      });
      await profileApi.postEducationDetails(payload, certificateFile, docCode);
      toast.success("Education details saved successfully");
      onSave(); //  NEW: Keep accordion open on this item after save
      setLocalDirty(false);
      onDirtyChange(false);

      if (parsedId) {
        try {
          dispatch(removeParsedEducationById(parsedId));

          if (refreshEducation) {
            try {
              await refreshEducation(false);
            } catch (refreshErr) {
              console.error(
                "Failed to refresh education details after save",
                refreshErr,
              );
            }
          }
        } catch (e) {
          console.warn("Failed to remove parsed education from redux by id", e);
        }
      } else {
        if (refreshEducation) {
          try {
            await refreshEducation(true);
          } catch (refreshErr) {
            console.error(
              "Failed to refresh education details after save",
              refreshErr,
            );
          }
        }
      }
    } catch (err) {
      console.error(err);
      const backendMessage =
        err?.response?.data?.message ||
        "Failed to save education";

      toast.error(backendMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredSpecializations = useMemo(() => {
    if (!formData.university) return [];

    return (
      masterData?.specializations?.filter(
        (s) =>
          s.educationQualificationsId === formData.university &&
          s.specializationName?.trim().toLowerCase() !== "other related fields",
      ) || []
    );
  }, [formData.university, masterData.specializations]);

  if (!masterData) {
    return null; // THIS LINE FIXES CRASHES
  }

  const clearErrors = (...keys) => {
    setFormErrors((prev) => {
      const updated = { ...prev };
      keys.forEach((k) => delete updated[k]);
      return updated;
    });
  };

  const BOARD_LEVEL_IDS = masterData.educationLevels
    .filter((e) => e.docCode === "BOARD")
    .map((e) => e.documentTypeId);

  const shouldShowSpecialization =
    formData.educationLevel &&
    !BOARD_LEVEL_IDS.includes(formData.educationLevel);

  const BOARD_LABEL_LEVELS = masterData.educationLevels
    .filter((e) => ["BOARD", "INTER"].includes(e.docCode))
    .map((e) => e.documentTypeId);

  const isBoardLevel = BOARD_LABEL_LEVELS.includes(formData.educationLevel);

  const boardLabel = isBoardLevel ? "Board" : "Course";
  const boardPlaceholder = isBoardLevel ? "Select Board" : "Select Course";

  const selectedBoard = masterData?.boards?.find(
    (b) => b.educationQualificationsId === formData.university,
  );

  const isOthersSelected =
    selectedBoard?.qualificationName?.toLowerCase() === "others";

  const otherFieldLabel = isBoardLevel ? "Board Name" : "Course Name";

  const selectedSpecialization = masterData?.specializations?.find(
    (s) => s.specializationId === formData.specialization,
  );

  const isOtherSpecializationSelected =
    selectedSpecialization?.specializationName?.toLowerCase() === "others";
  const selectedIntegratedSpecialization1 = integratedSpecializations[0]?.find(
    (item) => item.specializationId === integratedSpecialization1,
  );

  const selectedIntegratedSpecialization2 = integratedSpecializations[1]?.find(
    (item) => item.specializationId === integratedSpecialization2,
  );

  const isOtherIntegratedSpecialization1 =
    selectedIntegratedSpecialization1?.specializationName
      ?.trim()
      .toLowerCase() === "others";

  const isOtherIntegratedSpecialization2 =
    selectedIntegratedSpecialization2?.specializationName
      ?.trim()
      .toLowerCase() === "others";
  const fetchIntegratedSpecializations = async (
    icGroups,
    preserveSelections = false,
  ) => {
    if (!Array.isArray(icGroups) || !icGroups.length) {
      setIntegratedSpecializations({
        0: [],
        1: [],
      });
      return;
    }

    try {
      const groupIds = [...icGroups]
        .sort((a, b) => Number(a.key) - Number(b.key))
        .map((group) => group.value)
        .filter(Boolean);

      const response = await masterApi.getSpecializations(groupIds);

      const data = Array.isArray(response?.data) ? response.data : [];

      const grouped = {
        0: [],
        1: [],
      };

      groupIds.forEach((groupId, index) => {
        grouped[index] = data.filter(
          (item) =>
            item.educationQualificationsId === groupId &&
            item.specializationName?.trim().toLowerCase() !==
              "other related fields",
        );
      });

      setIntegratedSpecializations(grouped);

      if (!preserveSelections) {
        setIntegratedSpecialization1("");
        setIntegratedSpecialization2("");
      }
    } catch (error) {
      console.error("Failed to fetch integrated specializations", error);

      setIntegratedSpecializations({
        0: [],
        1: [],
      });
    }
  };

  const handleEducationDigiLocker = async () => {
    try {
      const selectedEducationLevel = masterData.educationLevels.find(
        (level) => level.documentTypeId === formData.educationLevel,
      );

      const docCode = selectedEducationLevel?.docCode;

      // if (!docCode) {
      //   toast.error("Please select Education Level");
      //   return;
      // }
      //  const docCode = formData.educationDocCode;
      const result = await profileApi.initializeDigiLockerConsent(
        "issued",
        docCode,
        activeStep,
      );

      if (result.success && result.data) {
        // window.open(result.data.redirect_uri, "_blank");
        window.location.href = result.data.redirect_uri;
      } else {
        toast.error("Unable to launch DigiLocker");
      }
    } catch (error) {
      toast.error("Failed to connect to DigiLocker");
    }
  };

  return (
    <form className="row g-4 formfields pt-2" onSubmit={handleSubmit}>
      <div className="col-md-3 col-sm-12 mt-3">
        <label className="form-label">
          Education Level <span className="text-danger">*</span>
        </label>
        <select
          id="educationLevel"
          className={`form-select ${formErrors.educationLevel ? "is-invalid" : ""}`}
          value={formData.educationLevel}
          disabled={disableEducationLevel}
          onChange={handleChange}
        >
          <option value="">Select Education Level</option>
          {masterData?.educationLevels.map((e) => (
            <option key={e.documentTypeId} value={e.documentTypeId}>
              {e.documentName}
            </option>
          ))}
        </select>
        {formErrors.educationLevel && (
          <div className="invalid-feedback">{formErrors.educationLevel}</div>
        )}
      </div>
      {/* College */}
      <div className="col-md-3 col-sm-12 mt-3">
        <label className="form-label">
          School/College <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="college"
          className={`form-control ${formErrors.college ? "is-invalid" : ""}`}
          value={formData.college}
          onBeforeInput={(e) => {
            if (!/^[A-Za-z ]$/.test(e.data)) e.preventDefault();
          }}
          onPaste={(e) => {
            if (!/^[A-Za-z ]+$/.test(e.clipboardData.getData("text"))) {
              e.preventDefault();
            }
          }}
          onChange={handleChange}
          maxLength={200}
        />
        {formErrors.college && (
          <div className="invalid-feedback">{formErrors.college}</div>
        )}
      </div>

      {!isBoardLevel && (
        <div className="col-md-3 col-sm-12 mt-3">
          <label htmlFor="universityName" className="form-label">
            University
          </label>

          <input
            type="text"
            id="universityName"
            className={`form-control ${formErrors.universityName ? "is-invalid" : ""}`}
            value={formData.universityName}
            onBeforeInput={(e) => {
              if (!/^[A-Za-z ]$/.test(e.data)) e.preventDefault();
            }}
            onPaste={(e) => {
              if (!/^[A-Za-z ]+$/.test(e.clipboardData.getData("text"))) {
                e.preventDefault();
              }
            }}
            onChange={handleChange}
            maxLength={200}
          />

          {formErrors.universityName && (
            <div className="invalid-feedback">{formErrors.universityName}</div>
          )}
        </div>
      )}

      {/* University */}
      {showBoard && (
        <div className="col-md-3 col-sm-12 mt-3">
          <label htmlFor="university" className="form-label">
            {boardLabel} <span className="text-danger">*</span>
          </label>
          <select
            id="university"
            className={`form-select ${formErrors.university ? "is-invalid" : ""}`}
            value={formData.university}
            disabled={!formData.educationLevel}
            onChange={handleChange}
          >
            <option value="">{boardPlaceholder}</option>
            {masterData?.boards
              .filter(
                (b) =>
                  b.levelId === formData.educationLevel &&
                  !b.qualificationName?.toLowerCase().startsWith("any"),
              )
              .map((e) => (
                <option
                  key={e.educationQualificationsId}
                  value={e.educationQualificationsId}
                >
                  {e.qualificationName}
                </option>
              ))}
          </select>
          {formErrors.university && (
            <div className="invalid-feedback">{formErrors.university}</div>
          )}
        </div>
      )}

      {isOthersSelected && (
        <div className="col-md-3 col-sm-12 mt-3">
          <label id="otherQualification" className="form-label">
            {otherFieldLabel} <span className="text-danger">*</span>
          </label>

          <input
            type="text"
            id="otherQualification"
            className={`form-control ${formErrors.otherQualification ? "is-invalid" : ""}`}
            value={formData.otherQualification}
            onChange={handleChange}
            maxLength={200}
          />

          {formErrors.otherQualification && (
            <div className="invalid-feedback">
              {formErrors.otherQualification}
            </div>
          )}
        </div>
      )}

      {/* From */}
      {!isBoardLevel && (
        <div className="col-md-3 col-sm-12 mt-3">
          <label className="form-label">
            From <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            id="from"
            className={`form-control ${formErrors.from || formErrors.dateRange ? "is-invalid" : ""}`}
            value={formData.from}
            onChange={handleChange}
            max={new Date().toISOString().split("T")[0]}
          />
          {formErrors.from && (
            <div className="invalid-feedback">{formErrors.from}</div>
          )}
          {formErrors.dateRange && !formErrors.from && !formErrors.to && (
            <div className="invalid-feedback">{formErrors.dateRange}</div>
          )}
        </div>
      )}

      {/* To */}
      <div className="col-md-3 col-sm-12 mt-3">
        <label className="form-label">
          {isBoardLevel ? "Date of issuance of certificate" : "To"}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="date"
          id="to"
          className={`form-control ${formErrors.to || formErrors.dateRange ? "is-invalid" : ""}`}
          onChange={handleChange}
          value={formData.to}
          min={!isBoardLevel ? formData.from || undefined : undefined}
          max={new Date().toISOString().split("T")[0]}
        />
        {formErrors.to && (
          <div className="invalid-feedback">{formErrors.to}</div>
        )}
        {formErrors.dateRange && !formErrors.from && !formErrors.to && (
          <div className="invalid-feedback">{formErrors.dateRange}</div>
        )}
      </div>

      {/* Percentage */}
      <div className="col-md-3 col-sm-12 mt-3">
        <label className="form-label">
          Percentage <span className="text-danger">*</span>
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          inputMode="decimal"
          id="percentage"
          className={`form-control ${formErrors.percentage ? "is-invalid" : ""}`}
          value={formData.percentage}
          onChange={handlePercentageChange}
        />
        {formErrors.percentage && (
          <div className="invalid-feedback">{formErrors.percentage}</div>
        )}
      </div>
      {showSpecialization &&
        shouldShowSpecialization &&
        selectedBoard?.isIntegratedCourse === true && (
          <>
            {/* Specialization 1 */}
            <div className="col-md-3 col-sm-12 mt-3">
              <label className="form-label">Course 1 Specialization</label>

              <select
                id="integratedSpecialization1"
                className="form-select"
                value={integratedSpecialization1}
                onChange={(e) => {
                  const value = e.target.value;

                  setIntegratedSpecialization1(value);

                  const selected = integratedSpecializations[0]?.find(
                    (item) => item.specializationId === value,
                  );

                  const isOthers =
                    selected?.specializationName?.trim().toLowerCase() ===
                    "others";

                  setFormData((prev) => ({
                    ...prev,
                    specialization: value,
                    otherSpecialization: isOthers
                      ? prev.otherSpecialization
                      : "",

                    // reset specialization 2
                    specialization2: "",
                    otherSpecialization2: "",
                  }));

                  setIntegratedSpecialization2("");

                  markDirty();
                }}
                disabled={!formData.university}
              >
                <option value="">Select Course 1 Specialization</option>

                {integratedSpecializations[0]?.map((item) => (
                  <option
                    key={item.specializationId}
                    value={item.specializationId}
                  >
                    {item.specializationName}
                  </option>
                ))}
              </select>
            </div>
            {isOtherIntegratedSpecialization1 && (
              <div className="col-md-3 col-sm-12 mt-3">
                <label className="form-label">
                  Other Course 1 Specialization{" "}
                  <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  id="otherSpecialization"
                  className={`form-control ${
                    formErrors.otherSpecialization ? "is-invalid" : ""
                  }`}
                  value={formData.otherSpecialization}
                  onChange={handleChange}
                  maxLength={200}
                />
                {formErrors.otherSpecialization && (
                  <div className="invalid-feedback">
                    {formErrors.otherSpecialization}
                  </div>
                )}
              </div>
            )}
            {/* Specialization 2 */}
            <div className="col-md-3 col-sm-12 mt-3">
              <label className="form-label">Course 2 Specialization</label>

              <select
                id="integratedSpecialization2"
                className="form-select"
                value={integratedSpecialization2}
                onChange={(e) => {
                  const value = e.target.value;

                  setIntegratedSpecialization2(value);

                  const selected = integratedSpecializations[1]?.find(
                    (item) => item.specializationId === value,
                  );

                  const isOthers =
                    selected?.specializationName?.trim().toLowerCase() ===
                    "others";

                  setFormData((prev) => ({
                    ...prev,
                    specialization2: value,
                    otherSpecialization2: isOthers
                      ? prev.otherSpecialization2
                      : "",
                  }));

                  markDirty();
                }}
                disabled={!integratedSpecialization1}
              >
                <option value="">Select Course 2 Specialization</option>

                {integratedSpecializations[1]
                  ?.filter(
                    (item) =>
                      item.specializationId !== integratedSpecialization1,
                  )
                  .map((item) => (
                    <option
                      key={item.specializationId}
                      value={item.specializationId}
                    >
                      {item.specializationName}
                    </option>
                  ))}
              </select>
            </div>
            {isOtherIntegratedSpecialization2 && (
              <div className="col-md-3 col-sm-12 mt-3">
                <label className="form-label">
                  Other Course 2 Specialization{" "}
                  <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  id="otherSpecialization2"
                  className={`form-control ${
                    formErrors.otherSpecialization2 ? "is-invalid" : ""
                  }`}
                  value={formData.otherSpecialization2}
                  onChange={handleChange}
                  maxLength={200}
                />
                {formErrors.otherSpecialization2 && (
                  <div className="invalid-feedback">
                    {formErrors.otherSpecialization2}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      {showSpecialization &&
        shouldShowSpecialization &&
        !selectedBoard?.isIntegratedCourse && (
          <div className="col-md-3 col-sm-12 mt-3">
            <label className="form-label">Specialization</label>
            <select
              id="specialization"
              className={`form-select ${formErrors.specialization ? "is-invalid" : ""}`}
              value={formData.specialization}
              onChange={handleChange}
              disabled={!formData.university}
            >
              <option value="">Select Specialization</option>
              {filteredSpecializations.map((e) => (
                <option key={e.specializationId} value={e.specializationId}>
                  {e.specializationName}
                </option>
              ))}
            </select>
            {formErrors.specialization && (
              <div className="invalid-feedback">
                {formErrors.specialization}
              </div>
            )}
          </div>
        )}

      {showSpecialization &&
        shouldShowSpecialization &&
        !selectedBoard?.isIntegratedCourse &&
        isOtherSpecializationSelected && (
          <div className="col-md-3 col-sm-12 mt-3">
            <label className="form-label">
              Other Specialization <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="otherSpecialization"
              className={`form-control ${formErrors.otherSpecialization ? "is-invalid" : ""}`}
              value={formData.otherSpecialization}
              onChange={handleChange}
              maxLength={200}
            />
            {formErrors.otherSpecialization && (
              <div className="invalid-feedback">
                {formErrors.otherSpecialization}
              </div>
            )}
          </div>
        )}

      <div className="col-md-3 col-sm-12 mt-3">
        <label className="form-label">
          Education Type <span className="text-danger">*</span>
        </label>
        <select
          id="educationType"
          className={`form-select ${formErrors.educationType ? "is-invalid" : ""}`}
          value={formData.educationType}
          onChange={handleChange}
        >
          <option value="">Select Education Type</option>
          {masterData?.educationTypes.map((e) => (
            <option key={e.educationTypeId} value={e.educationTypeId}>
              {e.educationType}
            </option>
          ))}
        </select>
        {formErrors.educationType && (
          <div className="invalid-feedback">{formErrors.educationType}</div>
        )}
      </div>

      <div className="col-md-3 col-sm-12 mt-3">
        <label htmlFor="eduCert" className="form-label">
          Education Certificate <span className="text-danger">*</span>
        </label>
        {/*
          {!certificateFile && !existingDocument && (
            <div
              className="border rounded d-flex flex-column align-items-center justify-content-center"
              style={{
                minHeight: "100px",
                cursor: "pointer",
                opacity: 1,
              }}
              //onClick={handleBrowse}
              onClick={() => setShowUploadOptions(true)}
            >
              <FontAwesomeIcon icon={faUpload} className="me-2 text-secondary" />
              <div
                className="mt-2"
                style={{ color: "#7b7b7b", fontWeight: "500" }}
              >
                Click to upload
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                PDF, JPG or PNG (Max 2MB)
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          )}
        */}

        {!certificateFile && !existingDocument && (
          <div
            className="border rounded p-3"
            style={{
              minHeight: "100px",
              background: "#f8f9fa",
            }}
          >
            <div className="text-center mb-3">
              <FontAwesomeIcon
                icon={faUpload}
                className="text-secondary"
                size="lg"
              />

              <div
                className="mt-2"
                style={{
                  color: "#7b7b7b",
                  fontWeight: "500",
                }}
              >
                Select Upload Option
              </div>

              <div className="text-muted" style={{ fontSize: "12px" }}>
                PDF, JPG or PNG (Max 2MB)
              </div>
            </div>

            <div className="d-grid gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleBrowse}
              >
                Upload from Device
              </button>

              {/* <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleEducationDigiLocker}
              >
                Upload from DigiLocker
              </button> */}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        )}

        {existingDocument && !certificateFile && (
          <div
            className="uploaded-file-box p-3 d-flex justify-content-between align-items-center"
            style={{
              border: "2px solid #bfc8e2",
              borderRadius: "8px",
              background: "#f7f9fc",
            }}
          >
            {/* LEFT SIDE: Check icon + File name + size */}
            <div className="d-flex align-items-center">
              <img
                src={greenCheck}
                alt="Check"
                style={{
                  fontSize: "22px",
                  marginRight: "10px",
                  width: "22px",
                  height: "22px",
                }}
              />
              <div>
                <div style={{ fontWeight: 600, color: "#42579f" }}>
                  {existingDocument.displayName ?? existingDocument.fileName}
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: View / Edit / Delete */}
            <div className="d-flex gap-2">
              <div onClick={() => handleEyeClick(existingDocument.fileUrl)}>
                <img
                  src={viewIcon}
                  alt="View"
                  style={{ width: "25px", cursor: "pointer" }}
                />
              </div>
              {/* Delete */}
              <img
                src={deleteIcon}
                alt="Delete"
                style={{ width: "25px", cursor: "pointer" }}
                onClick={() => {
                  setExistingDocument(null);
                  setCertificateFile(null);
                }}
              />
            </div>
          </div>
        )}

        {certificateFile && (
          <div
            className="uploaded-file-box p-3 d-flex justify-content-between align-items-center"
            style={{
              border: "2px solid #bfc8e2",
              borderRadius: "8px",
              background: "#f7f9fc",
            }}
          >
            {/* LEFT SIDE: Check icon + File name + size */}
            <div className="d-flex align-items-center">
              <img
                src={greenCheck}
                alt="Check"
                style={{
                  fontSize: "22px",
                  marginRight: "10px",
                  width: "22px",
                  height: "22px",
                }}
              />
              <div>
                <div
                  style={{ fontWeight: 600, color: "#42579f" }}
                  className="wraptext"
                >
                  {certificateFile?.name}
                </div>
                <div className="text-muted" style={{ fontSize: "12px" }}>
                  {formatFileSize(certificateFile.size)}
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: View / Edit / Delete */}
            <div className="d-flex gap-2">
              {/* View */}
              <img
                src={viewIcon}
                alt="View"
                style={{ width: "25px", cursor: "pointer" }}
                onClick={() =>
                  window.open(URL.createObjectURL(certificateFile), "_blank")
                }
              />

              {/* Delete */}
              <img
                src={deleteIcon}
                alt="Delete"
                style={{ width: "25px", cursor: "pointer" }}
                onClick={() => setCertificateFile(null)}
              />
            </div>
          </div>
        )}
        {formErrors.certificateFile && (
          <div className="invalid-feedback d-block">
            {formErrors.certificateFile}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
        <div className="d-flex">
          <img
            src={bulbIcon}
            alt="Bulb"
            style={{
              width: "25px",
              height: "25px",
              marginTop: "6px",
              marginRight: "5px",
            }}
          />
          <p className="orange_text mt-2 mb-0">
            Please convert CGPA into percentage, if applicable.
          </p>
        </div>
        <div>
          <button
            type="submit"
            className="btn btn-primary mt-3"
            // disabled={isSaving || !localDirty}
            disabled={isSaving}
            style={{
              backgroundColor: "#ff7043",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              color: "#fff",
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      <IdProofUploadModal
        show={showUploadOptions}
        onHide={() => setShowUploadOptions(false)}
        onUploadFromBrowser={() => handleBrowse()}
        onUploadFromDigiLocker={handleEducationDigiLocker}
        loading={isSaving}
      />
    </form>
  );
});

export default EducationForm;
